/**
 * 학생 v2 쓰기 창(모달)의 공통 틀 — 입력 칸 · 저장/취소 · 서버 거절 문구(설계 §5-2) · 코드·반·강사·학교 목록.
 * 창은 저장소 공용 Modal(common/Modal.js)에 담는다. 저장에 성공하면 바뀜을 알려 목록·상세·통계가 다시 부른다.
 */
import React, { useRef, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { Modal } from '../../common/Modal';
import { AcademyButton, ActionButton, inputCls, saveIcon } from '../controls';
import { describeAcademyError, listClasses, listCodes, listSchools, listStaff } from '../academyApi';
import { notifyAcademyChanged, useAcademyVersion, useAsync } from '../ui';
import { schoolLevelOfGrade } from '../periodDates';

export { DateInput, TimeInput, SelectInput, MultiSelectInput, TextInput, TextArea, inputBase, inputCls, compactInputBase } from '../controls';

export function Field({ label, hint, children, required = false }) {
  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control -- 칸은 children 으로 들어온다
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-gray-700">{label}{required && <span className="ml-0.5 text-danger">*</span>}</span>
      {children}
      {hint && <span className="mt-1 block text-xs text-gray-500">{hint}</span>}
    </label>
  );
}

/**
 * 비밀번호 칸 — 가리기 · 보기(백엔드 2단계 전달 §4-A). 학생 계정의 비밀번호라 브라우저가 매니저 본인의 저장된 비밀번호를
 * 채워 넣지 않게 new-password 로 둔다.
 */
export function PasswordInput({ value, onChange, ...rest }) {
  const [shown, setShown] = useState(false);
  return (
    <span className="relative block">
      <input
        type={shown ? 'text' : 'password'}
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        autoComplete="new-password"
        className={`${inputCls} pr-20`}
        {...rest}
      />
      <ActionButton
        onClick={() => setShown((v) => !v)}
        aria-pressed={shown}
        className="absolute inset-y-0 right-2 my-auto"
      >
        {shown ? '가리기' : '보기'}
      </ActionButton>
    </span>
  );
}

/** 출결번호 칸 — 숫자만, 4자리까지(백엔드 2단계 전달 §3 출결번호) */
export function StudentNoInput({ value, onChange, ...rest }) {
  return (
    <input
      type="text"
      inputMode="numeric"
      maxLength={4}
      value={value || ''}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, '').slice(0, 4))}
      className={inputCls}
      {...rest}
    />
  );
}
/** 출결번호가 비었거나 4자리인가 — 1~3자리는 저장을 잠근다 */
export const studentNoOk = (v) => !v || /^\d{4}$/.test(v);
/** 출결번호 칸 아래 글 — 덜 채웠으면 그것을 말한다 */
export const studentNoHint = (v) => (studentNoOk(v) ? '4자리 숫자' : '4자리를 모두 적어 주세요.');
/** 이름 · 비밀번호 규칙 — 서버와 같은 말(백엔드 2단계 전달 §3) */
export const NAME_RULE = '2~20자 한글·영문·숫자 — 띄어쓰기·특수문자 없이';
export const PASSWORD_RULE = '8~24자 — 영문 대문자·소문자·숫자를 하나 이상씩, 띄어쓰기 없이';

/** 안내 글 — 「반에 있는 마지막 날」 같은 계산 결과를 칸 아래에 보인다(설계 D4) */
export function Note({ children, tone = 'muted' }) {
  const cls = tone === 'warn' ? 'bg-yellow-50 text-yellow-800' : 'bg-gray-50 text-gray-600';
  return <p className={`rounded-xl px-3 py-2 text-xs leading-relaxed ${cls}`}>{children}</p>;
}

/** 퇴원일 당일 · 뒤에 시작해 퇴원을 막는 줄 — 반이면 「반 이름 시작일~」, 휴원이면 「휴원 시작일~」(백엔드 5단계 전달 §4-F) */
const PAUSE_TABLE = 'academy_student_pauses';
const blockingRowText = (r) => `${r.table === PAUSE_TABLE ? '휴원' : r.class_name || '반 편성'} ${r.start_on}~`;

/**
 * 서버 거절 — 사유별 문구(설계 §5-2) + 겹친 행 · 학생별 실패.
 * 재원 기간 정정의 409 outside_tenure 는 재원 기간 밖으로 나가는 반 편성을 같은 자리에 「걸린 편성: 반 이름 시작~끝」으로(백엔드 4단계 전달 §4-E),
 * 휴원이 걸리면 「걸린 휴원: 시작~끝」으로(휴원 줄은 table 이 academy_student_pauses — 백엔드 5단계 전달 §3).
 * 퇴원의 409 rows_after_withdraw 는 먼저 지우거나 고칠 줄(data.rows)을 「걸린 기록: …」으로
 */
export function ErrorBox({ error, names = {} }) {
  if (!error) return null;
  const conflicts = error.data?.conflicts || [];
  const failures = error.data?.failures || [];
  const blocking = error.reason === 'rows_after_withdraw' ? error.data?.rows || [] : [];
  const enrollments = error.reason === 'outside_tenure';
  const pauses = enrollments && conflicts.some((c) => c.table === PAUSE_TABLE);
  return (
    <div role="alert" className="rounded-xl bg-red-50 px-3 py-2 text-xs text-danger">
      <p>{describeAcademyError(error)}</p>
      {conflicts.length > 0 && (
        <p className="mt-1 text-red-700">
          {pauses ? '걸린 휴원: ' : enrollments ? '걸린 편성: ' : '겹친 기록: '}
          {conflicts.map((c) => `${enrollments && c.class_name ? `${c.class_name} ` : ''}${c.start_on}~${(Object.hasOwn(c, 'last_day') ? c.last_day : c.end_on) || ''}`).join(', ')}
        </p>
      )}
      {blocking.length > 0 && <p className="mt-1 text-red-700">걸린 기록: {blocking.map(blockingRowText).join(', ')}</p>}
      {failures.length > 0 && (
        <ul className="mt-1 list-inside list-disc text-red-700">
          {/* 이름을 모르면 「학생」 — student_id 는 화면에 내지 않는다(백엔드 2단계 전달 §4-C) */}
          {failures.map((f) => <li key={f.student_id}>{names[f.student_id] || '학생'} — {describeAcademyError({ reason: f.reason })}</li>)}
        </ul>
      )}
    </div>
  );
}

/**
 * 쓰기 창 — onSubmit 이 끝나면 바뀜을 알리고 닫는다. 실패하면 창 안에 사유 문구를 보이고 입력은 그대로 둔다.
 * onSubmit 이 false 를 반환하면 다음 단계를 위해 창을 유지한다. 이때 바뀜 알림은 호출자가 닫을 때 보낸다.
 * canSubmit 이 거짓이면 저장 단추가 잠긴다. lockWhileBusy 는 단계별 작업 중 입력·닫기도 잠근다.
 */
export function FormModal({ isOpen = true, onClose, title, onSubmit, submitLabel = '저장', cancelLabel = '취소', danger = false, canSubmit = true, size = 'md', children, names, top, fixedHeight = false, lockWhileBusy = false }) {
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState(null);
  const bodyRef = useRef(null);
  const submit = async () => {
    if (busyRef.current || !canSubmit) return;
    const invalid = bodyRef.current?.querySelector('input:invalid, select:invalid, textarea:invalid');
    if (invalid) { invalid.reportValidity(); invalid.focus(); return; }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const result = await onSubmit();
      if (result === false) return;
      notifyAcademyChanged();
      onClose();
    } catch (e) {
      setError(e);
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };
  const footer = (
    <div className="flex w-full items-center justify-end gap-2">
      <AcademyButton size="sm" variant="secondary" onClick={onClose} disabled={lockWhileBusy && busy}>{cancelLabel}</AcademyButton>
      <AcademyButton
        size="sm"
        variant={danger ? 'danger' : 'primary'}
        icon={danger ? undefined : saveIcon}
        onClick={submit}
        disabled={busy || !canSubmit}
        loading={busy}
      >
        {busy ? '저장 중…' : submitLabel}
      </AcademyButton>
    </div>
  );
  return (
    <Modal
      isOpen={isOpen} onClose={() => { if (!lockWhileBusy || !busyRef.current) onClose(); }} title={title} size={size} footer={footer}
      fixedHeight={fixedHeight}
      maxHeight={fixedHeight ? `min(${size === 'lg' ? 760 : 640}px, 88vh)` : '88vh'}
      bodyClassName={fixedHeight ? 'min-h-0 overflow-y-auto [scrollbar-gutter:stable]' : ''}
    >
      <fieldset ref={bodyRef} disabled={lockWhileBusy && busy} className="min-w-0 space-y-3">
        {top}
        {children}
        <ErrorBox error={error} names={names} />
      </fieldset>
    </Modal>
  );
}

// ── 고르기 재료 — 창마다 같은 목록을 쓰므로 받은 것을 기억한다. 저장이 있으면 다시 받는다 ──

/**
 * 기억은 바뀜 번호(useAcademyVersion)와 함께 둔다. 저장(notifyAcademyChanged)이 있으면 번호가 올라 다음에 여는 창이
 * 다시 받는다 — 설정에서 학교를 만들거나 고치거나 지운 뒤, 사유·분류를 만들거나 고치거나 감추거나 지운 뒤 학생 창 드롭다운에 바로
 * 보이게(백엔드 0단계 전달 §4-B). 예전에는 페이지를 새로고침할 때까지 첫 응답을 썼다.
 * 기억이 부른 쪽보다 새것이면 그대로 쓴다 — 바뀜 소식을 아직 못 받은 칸이 새 응답을 옛것으로 덮지 않게.
 * 실패한 요청은 기억에서 지운다(다음에 다시 부른다).
 */
const memo = new Map();
const remember = (key, version, fn) => {
  const hit = memo.get(key);
  if (hit && hit.version >= version) return hit.promise;
  const promise = fn().catch((e) => {
    if (memo.get(key)?.promise === promise) memo.delete(key);
    throw e;
  });
  memo.set(key, { version, promise });
  return promise;
};

/**
 * 마지막으로 받은 목록 — 저장 뒤 다시 받기가 실패해도 고르던 목록을 비우지 않는다.
 * useAsync 는 실패하면 data 를 null 로 두는데, 예전(페이지 동안 첫 응답을 그대로 쓰던 때)에는 한 번 받은 목록이 사라지는 일이 없었다.
 */
function useLastGood(r) {
  const last = useRef(null);
  if (r.data != null) last.current = r.data;
  return last.current;
}

/**
 * 코드 묶음(사유·분류) — items 와 ready(한 번이라도 받았는가).
 * 받는 중이거나 실패해 비어 있는 것을 「값이 없다」로 읽지 않으려고 ready 를 함께 준다(빈 목록 안내 — EmptyCodesNote).
 */
export function useCodeList(group) {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const r = useAsync(() => remember(`codes-${group}`, version, () => listCodes(group, {}, getAccessToken)), `codes-${group}-${version}`);
  const data = useLastGood(r);
  return { items: data || [], ready: data != null };
}
/** 반 목록 — state: open(운영) · all. enabled 가 거짓이면 부르지 않는다(반을 쓰지 않는 창 — 반 경로는 3단계에 열림) */
export function useClassList(state = 'open', version = 0, enabled = true) {
  const { getAccessToken } = useAuth();
  const r = useAsync(() => (enabled ? listClasses({ state }, getAccessToken) : Promise.resolve([])), `classes-${state}-${version}-${enabled}`);
  return r.data || [];
}
export function useTeachers() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const r = useAsync(() => remember('teachers', version, () => listStaff({ kind: 'TEACHER' }, getAccessToken)), `teachers-${version}`);
  return useLastGood(r) || [];
}
/**
 * 담임 고르기 — 강사 · 매니저 · 관리자(백엔드 3단계 전달 §1 · §4-C). kind 없이 불러 매니저도 받는다(서버는 관리자도 MANAGER 로 준다).
 * 출결 「수업한 강사」(useTeachers)와 기억을 나누지 않는다 — 그쪽은 강사만이다(7단계에서 정함).
 */
export function useHomerooms() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const r = useAsync(() => remember('homerooms', version, () => listStaff({}, getAccessToken)), `homerooms-${version}`);
  return useLastGood(r) || [];
}
/** 담임 고르기 글자 — 매니저는 「이름 (매니저)」 */
export const homeroomLabel = (s) => (s.kind === 'MANAGER' ? `${s.name} (매니저)` : s.name);

export function useSchools() {
  const { getAccessToken } = useAuth();
  const version = useAcademyVersion();
  const r = useAsync(() => remember('schools', version, () => listSchools({}, getAccessToken)), `schools-${version}`);
  return useLastGood(r) || [];
}

const LEVEL_NAME = { ELEM: '초등', MIDDLE: '중등', HIGH: '고등' };

/**
 * 학교 고르기 글자 — 「이름 (학교급 · 지역)」, 지역이 없으면 「이름 (학교급)」(백엔드 0단계 전달 §4-C).
 * 이름·학교급이 같고 지역만 다른 학교를 고르기에서 가를 수 있게 한다. 학교급 이름은 설정 화면의 학교 표와 같다(초등·중등·고등).
 */
export function schoolOptionLabel(school) {
  const parts = [LEVEL_NAME[school.school_level], String(school.region ?? '').trim()].filter(Boolean);
  return parts.length ? `${school.name} (${parts.join(' · ')})` : school.name;
}
/** 학교 고르기 칸의 선택지 — 새 학생 창 · 학교·학년 변경 창 · 학교 기간 정정 창이 같이 쓴다 */
export const schoolOptions = (schools) => schools.map((x) => [x.school_id, schoolOptionLabel(x)]);

/**
 * 사유·분류가 비었을 때 칸 아래 안내(백엔드 0단계 전달 §3-3 · §4-D).
 * 운영 값(퇴원·휴원·퇴반 사유, 메모 분류)은 서버 시드에 없어 처음에는 비어 있을 수 있다 — 필수 칸이면 저장 단추가 잠긴 까닭을 말하고,
 * 선택 칸이면 비워 두고 저장해도 된다고 말한다. 받기 전(ready 아님)에는 아무것도 보이지 않는다.
 * label 은 받침 없는 이름(퇴원 사유 · 휴원 사유 · 퇴반 사유 · 메모 분류)이라 조사는 「가」로 붙인다.
 */
export function EmptyCodesNote({ list, label, required = false }) {
  if (!list.ready || list.items.length > 0) return null;
  return (
    <span role="status" className="mt-1 block text-xs text-yellow-800">
      {required
        ? `${label}가 아직 없어 저장할 수 없습니다. `
        : `${label}가 아직 없습니다 — 고르지 않고 저장해도 됩니다. `}
      [반 · 기준 설정 › 사유·분류]에서 먼저 추가해 주세요.
    </span>
  );
}

/** 학년 고르기 — 1~14(설계 §4 번호표 + 14 비재학, 백엔드 2단계 전달 §4-D). 13 · 14 는 학교급이 없는 학년 */
export const GRADE_OPTIONS = Array.from({ length: 14 }, (_, i) => i + 1).map((g) => [
  String(g), g <= 6 ? `초${g}` : g <= 9 ? `중${g - 6}` : g <= 12 ? `고${g - 9}` : g === 13 ? '졸업(13)' : '비재학(14)',
]);

/** 학교급에 맞는 학년. 학교 미지정은 전체, 졸업·비재학은 어느 학교에서도 선택할 수 있다. */
export function gradeOptionsForSchool(school, currentGrade = '') {
  if (!LEVEL_NAME[school?.school_level]) return GRADE_OPTIONS;
  // 기존 기록의 학교급·학년 불일치는 자동으로 바꾸지 않는다. 학교를 바꿀 때만 맞지 않는 선택을 비운다.
  return GRADE_OPTIONS.filter(([value]) => Number(value) > 12
    || schoolLevelOfGrade(Number(value)) === school.school_level
    || value === String(currentGrade));
}
